// src/components/forms/fields/LocationSelectField.tsx

import React, { useState, useEffect } from 'react';
import { MapPin, Loader2, Navigation, Users } from 'lucide-react';
import { apiService as sharedApiService } from '../../../services/api';
import { Location } from '@prism-ehr/shared';

type LocationProvider = {
  id: string;
  first_name?: string;
  last_name?: string;
  title?: string;
};

type LocationWithProviders = Location & { providers?: LocationProvider[] };

interface LocationApiService {
  getOrganizationLocationsPublic: (
    organizationId: string,
    options?: { includeProviders?: boolean }
  ) => Promise<LocationWithProviders[]>;
}

interface LocationSelectFieldProps {
  organizationId: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  showAddress?: boolean;
  showDistance?: boolean;
  showMap?: boolean;
  showProviders?: boolean;
  placeholder?: string;
  apiService?: LocationApiService;
}

const LocationSelectField: React.FC<LocationSelectFieldProps> = ({
  organizationId,
  value,
  onChange,
  required = false,
  disabled = false,
  showAddress = true,
  showDistance = false,
  showMap = false,
  showProviders = false,
  placeholder = 'Select a location',
  apiService: injectedApiService
}) => {
  const apiService = injectedApiService || sharedApiService;
  const [locations, setLocations] = useState<LocationWithProviders[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<GeolocationCoordinates | null>(null);
  const [distances, setDistances] = useState<Record<string, number>>({});

  useEffect(() => {
    loadLocations();
  }, [organizationId, showProviders]);

  useEffect(() => {
    if (showDistance && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation(position.coords);
        },
        (error) => {
          console.error('Error getting user location:', error);
        }
      );
    }
  }, [showDistance]);

  useEffect(() => {
    if (userLocation && locations.length > 0) {
      calculateDistances();
    }
  }, [userLocation, locations]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      setError(null);

      const locationData = await apiService.getOrganizationLocationsPublic(organizationId, {
        includeProviders: showProviders
      });
      setLocations(locationData || []);
    } catch (err) {
      console.error('Error loading locations:', err);
      setError('Failed to load locations');
    } finally {
      setLoading(false);
    }
  };

  const calculateDistances = () => {
    if (!userLocation) return;

    // This is a placeholder for distance calculation
    // In production, you'd use a geocoding service to get lat/lng for each location
    // and calculate actual distances
    const distanceMap: Record<string, number> = {};
    locations.forEach(location => {
      // Placeholder: random distance for demo
      distanceMap[location.id] = Math.floor(Math.random() * 20) + 1;
    });
    setDistances(distanceMap);
  };

  const formatAddress = (location: Location) => {
    const addr = location.address;
    const parts = [
      addr.line1,
      addr.line2,
      `${addr.city}, ${addr.state} ${addr.zip}`
    ].filter(Boolean);
    return parts.join(', ');
  };

  const handleSelect = (locationId: string) => {
    onChange(locationId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4 border rounded-lg bg-gray-50">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400 mr-2" />
        <span className="text-gray-500">Loading locations...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 border border-red-200 rounded-lg bg-red-50">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <div className="p-4 border rounded-lg bg-gray-50">
        <p className="text-gray-500 text-sm">No locations available</p>
      </div>
    );
  }

  // If only one location, auto-select it
  if (locations.length === 1 && !value) {
    onChange(locations[0].id);
  }

  return (
    <div className="space-y-3">
      {/* Simple dropdown if not showing details */}
      {!showAddress && !showDistance && (
        <select
          value={value}
          onChange={(e) => handleSelect(e.target.value)}
          required={required}
          disabled={disabled || locations.length === 1}
          className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-[#0facd7] focus:border-[#0facd7] disabled:bg-gray-100"
        >
          <option value="">{placeholder}</option>
          {locations.map(location => (
            <option key={location.id} value={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      )}

      {/* Detailed cards if showing address or distance */}
      {(showAddress || showDistance) && (
        <div className="space-y-2">
          {locations.map(location => (
            <div
              key={location.id}
              onClick={() => !disabled && handleSelect(location.id)}
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                value === location.id
                  ? 'bg-blue-50 border-[#0facd7] shadow-sm'
                  : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-sm'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <h4 className="font-medium text-gray-900">{location.name}</h4>
                    {value === location.id && (
                      <span className="px-2 py-1 text-xs bg-[#0facd7] text-white rounded-full">
                        Selected
                      </span>
                    )}
                  </div>

                  {showAddress && (
                    <p className="text-sm text-gray-600 ml-6">
                      {formatAddress(location)}
                    </p>
                  )}

                  {showDistance && distances[location.id] && (
                    <div className="flex items-center gap-1 mt-2 ml-6">
                      <Navigation className="w-3 h-3 text-gray-400" />
                      <span className="text-xs text-gray-500">
                        {distances[location.id]} miles away
                      </span>
                    </div>
                  )}

                  {showProviders && location.providers && location.providers.length > 0 && (
                    <div className="flex items-start gap-1 mt-2 ml-6">
                      <Users className="w-3 h-3 text-gray-400 mt-0.5 shrink-0" />
                      <span className="text-xs text-gray-500">
                        {location.providers
                          .map(p => [p.title, p.first_name, p.last_name].filter(Boolean).join(' '))
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </div>
                  )}
                </div>

                {/* Selection indicator */}
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  value === location.id
                    ? 'border-[#0facd7] bg-[#0facd7]'
                    : 'border-gray-300'
                }`}>
                  {value === location.id && (
                    <div className="w-2 h-2 bg-white rounded-full" />
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Map container (placeholder) */}
      {showMap && value && (
        <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
          <div className="flex items-center justify-center h-48 bg-white rounded">
            <div className="text-center">
              <MapPin className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-500">
                Map integration would show location here
              </p>
              <p className="text-xs text-gray-400 mt-1">
                {locations.find(l => l.id === value)?.name}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelectField;